import React, { useContext, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import { configStore } from "../../../../../../contexts/ConfigContext";
import { StudentCollectionDetailsProps } from "../../../../../../components/StudentCollectionDetails/StudentCollectionDetails";
import Loader from "../../../../../../components/Loader/Loader";
import { useClassroomQuery } from "../../../api/queries";
import ContainerBlock from "../../../../../../design-system-components/ContainerBlock/ContainerBlock";
import dashboardMessages from "../../../dashboardMessages";
import UserTable from "./Components/UserTable";
import HelpInfo from "../../../../../../components/StudentCollectionDetails/HelpInfo/HelpInfo";
import { useUserExtra } from "../../../../../../hooks/queries/useUserExtra";
import { DismissableMessages } from "../../../../../../interfaces/Session";

import "./UserList.scss";

type Props = {
    classroomId: string;
    type: StudentCollectionDetailsProps["type"];
    optionsChildren?: React.ReactNode;
    footerChildren?: React.ReactNode;
};
const UserList = ({
    classroomId,
    type,
    optionsChildren,
    footerChildren,
}: Props) => {
    const intl = useIntl();
    const {
        config: { features },
    } = useContext(configStore);
    const [studentToEdit, setStudentToEdit] = useState<string>("");
    const { data: classroom } = useClassroomQuery(classroomId, type);
    const [extra, _] = useUserExtra();

    const editStudentHandler = (id: string) => () => {
        setStudentToEdit(id);
    };

    return (
        <ContainerBlock>
            <div className="user-list__title">
                <h2>{intl.formatMessage(dashboardMessages.students)}</h2>

                {optionsChildren}
            </div>
            {!features.studentManagement &&
                !extra.dismissedMessages?.includes(
                    DismissableMessages.dashboardHelpInfo
                ) && <HelpInfo />}

            <Loader isLoading={!classroom}>
                {classroom && (
                    <div className="user-list">
                        <table className="user-list__table">
                            <caption>
                                <VisuallyHidden as="span">
                                    {intl.formatMessage(messages.studentsList)}
                                </VisuallyHidden>
                            </caption>

                            <tbody>
                                <UserTable.Header
                                    showCodes={
                                        type === "classroom" &&
                                        features.studentManagement
                                    }
                                />

                                {classroom.students.map((student, index) => (
                                    <UserTable.Row
                                        key={student.id}
                                        type={type}
                                        classroom={classroom}
                                        student={student}
                                        studentToEdit={studentToEdit}
                                        onClickEdit={editStudentHandler(
                                            student.id
                                        )}
                                        onFinishEdit={editStudentHandler("")}
                                        index={index}
                                    />
                                ))}
                            </tbody>
                        </table>

                        {footerChildren}
                    </div>
                )}
            </Loader>
        </ContainerBlock>
    );
};
export default UserList;

const messages = defineMessages({
    studentsList: {
        id: "user-list-title",
        defaultMessage: "Students list",
    },
});
